import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { EagleCausa, EagleParam, EaglePunto, EagleSerieMultipla, EagleStoricoAllarmi, Taxonomy, EagleOverview, EagleSparkPunto } from '../../api/types'
import { usePolling, useAsync } from '../../hooks/useAsync'
import { localeCorrente, t } from '../../i18n/lingua'
import { Causa, VerdettoSonda } from '../CausaRadice'
import { timeAgo } from '../common'
import { FinestraParametro, type ParametroDaAprire } from '../FinestraParametro'
import { PuntoStato, StatoBadge, etichettaStato, tonoStato } from '../EagleStato'
import { etichettaParametro, formatConUnita, formatValore } from '../eagleTesti'
import { GraficoSerie, type SerieGrafico, type TipoGrafico, type ScalaY } from '../GraficoSerie'
import { GraficoStati } from '../GraficoStati'
import { Widget, type MisuraWidget } from '../Griglia'
import {
  BarraSoglie, Segmenti, Sparkline, TimelineIncidenti, durataBreve, formatIstante, formatOra, niceMax, segmentiDaConteggi,
  useAltezza, useLarghezza, useRiquadro, type Segmento, type Tono,
} from './graficiEagle'
import { MappaEagle } from './MappaEagle'
import { LINK_VUOTO, type LinkScelto } from './SceltaLink'
import { unitaDiMisura, type SerieScelta } from './SceltaSerie'

/**
 * I widget di Eagle per il cruscotto personalizzabile: quelli che servono a
 * chi guarda il monitoraggio — i numeri con l'andamento, la parete dei CI,
 * il grafico di un parametro a scelta, le classifiche con barra e sparkline,
 * la disponibilità con il traguardo dello SLA, gli incidenti su un asse del
 * tempo, il rispetto degli SLA, il carico delle sonde. Vanno nel catalogo
 * generale (gruppo «eagle») accanto ai quattro di base.
 *
 * Ogni widget legge le API del cruscotto, che sono in cache lato server e
 * costano una lettura per tutti: mai una chiamata per CI. I grafici piccoli
 * sono in `graficiEagle.tsx`; i colori di stato arrivano sempre con la
 * parola accanto.
 */

export interface OpzioneEagle {
  key: string
  label: () => string
  valori: { value: string; label: () => string }[]
  predefinito: string
  dinamica?: 'ci_param' | 'serie' | 'link' | 'vista_topologia' | 'bersaglio' | 'immagine'
  tipo?: 'select' | 'bool' | 'testo' | 'multilinea'
  aiuto?: () => string
  soloTipi?: string[]
  massimo?: number
}

const ogniMinuto = 60_000

/** La finestra dei widget che leggono panoramica e disponibilità (il Control Plane le dà a ore intere). */
export const ORE_EAGLE: OpzioneEagle = {
  key: 'hours', predefinito: '24', label: () => t('Finestra'),
  valori: [{ value: '1', label: () => t('1 ora') }, { value: '6', label: () => t('6 ore') }, { value: '24', label: () => t('24 ore') }, { value: '168', label: () => t('7 giorni') }],
}
/** La finestra dei grafici delle serie: anche sotto l'ora (le sonde aggregano a 5 minuti). */
export const ORE_SERIE: OpzioneEagle = {
  key: 'hours', predefinito: '6', label: () => t('Finestra'),
  valori: [
    { value: '0.25', label: () => t('15 minuti') }, { value: '0.5', label: () => t('30 minuti') }, { value: '1', label: () => t('1 ora') },
    { value: '3', label: () => t('3 ore') }, { value: '6', label: () => t('6 ore') }, { value: '24', label: () => t('24 ore') }, { value: '168', label: () => t('7 giorni') },
  ],
}
/** La finestra della sparkline delle classifiche: più corta, si legge in pochi pixel. */
const ORE_SPARK: OpzioneEagle = { ...ORE_SERIE, label: () => t('Andamento') }
const TIPO_TOP: OpzioneEagle = {
  key: 'kind', predefinito: 'cpu', label: () => t('Tipo di parametro'),
  valori: [
    { value: 'cpu', label: () => t('CPU') }, { value: 'memory', label: () => t('Memoria') }, { value: 'volume', label: () => t('Volumi') },
    { value: 'interface', label: () => t('Interfacce') }, { value: 'tcp', label: () => t('Porte TCP') }, { value: 'ping', label: () => t('Ping') },
    { value: 'ping_fast', label: () => t('Ping rapido') },
    { value: 'uptime', label: () => t('Tempo dall’avvio') },
  ],
}
/** The alarm history's window: the samples are kept seven days. */
const ORE_STORICO: OpzioneEagle = {
  key: 'hours', predefinito: '24', label: () => t('Periodo'),
  valori: [{ value: '24', label: () => t('24 ore') }, { value: '48', label: () => t('48 ore') }, { value: '168', label: () => t('7 giorni') }],
}
const FILTRO_STORICO: OpzioneEagle = {
  key: 'stati', predefinito: 'tutti', label: () => t('Mostra'),
  valori: [
    { value: 'tutti', label: () => t('tutti gli episodi') }, { value: 'gravi', label: () => t('solo critici e non risponde') },
    { value: 'giu', label: () => t('solo chi non rispondeva') },
  ],
}
/** In each tile, the ping response time of the last hour (the fast ping). */
const SPARK_PARETE: OpzioneEagle = {
  key: 'ping_spark', predefinito: 'false', tipo: 'bool', valori: [],
  label: () => t('In ogni riquadro il grafico del tempo di risposta del ping (ultima ora)'),
  aiuto: () => t('Con il «Ping rapido (latenza)» acceso: media, minimo e massimo di dodici pacchetti al minuto; con il ping normale: il miglior tempo dei tre pacchetti al minuto (serve la sonda aggiornata).'),
}

const FILTRO_PARETE: OpzioneEagle = {
  key: 'only', predefinito: 'all', label: () => t('Mostra'),
  valori: [{ value: 'all', label: () => t('tutti i CI') }, { value: 'problems', label: () => t('solo con problemi') }],
}
/** The wall by tag: «sede:milano» (that tag) or «sede» (any value), several
 *  separated by commas — all must be there. */
const TAG_PARETE: OpzioneEagle = {
  key: 'tags', predefinito: '', tipo: 'testo', label: () => t('Solo i CI con i tag'), valori: [],
  aiuto: () => t('chiave:valore oppure solo chiave (qualunque valore); più tag separati da virgola, tutti richiesti. Vuoto = tutti i CI.'),
}

/** The tags asked in an option, normalised. */
function tagRichiesti(testo: unknown): string[] {
  return String(testo ?? '').split(',').map((x) => x.trim().toLowerCase()).filter(Boolean)
}

/** Whether a CI carries every requested tag («chiave» matches any value of that key). */
function haITag(tags: string[] | undefined, richiesti: string[]): boolean {
  if (richiesti.length === 0) return true
  const propri = (tags ?? []).map((x) => x.toLowerCase())
  return richiesti.every((r) => propri.some((x) => x === r || (!r.includes(':') && x.startsWith(r + ':'))))
}

const MODO_VALORE: OpzioneEagle = {
  key: 'show', predefinito: 'valore', label: () => t('Mostra'),
  valori: [{ value: 'valore', label: () => t('il valore, nel colore dello stato') }, { value: 'stato', label: () => t('lo stato, con il valore sotto') }],
}
const SPARK_VALORE: OpzioneEagle = { key: 'spark', predefinito: 'true', tipo: 'bool', label: () => t('Il grafico dell’ultima ora sotto il valore'), valori: [] }

/** On the wall, the tiles that do not fit the screen come in pages, at a chosen pace. */
const PAGINE_PARETE: OpzioneEagle = {
  key: 'pagine_s', predefinito: '15', label: () => t('Sul sinottico: cambia pagina ogni'),
  valori: [
    { value: '0', label: () => t('mai: la parete scorre') }, { value: '10', label: () => t('10 secondi') },
    { value: '15', label: () => t('15 secondi') }, { value: '20', label: () => t('20 secondi') },
    { value: '30', label: () => t('30 secondi') }, { value: '60', label: () => t('60 secondi') },
  ],
  aiuto: () => t('Vale sul sinottico a parete: le tessere che non entrano nello schermo si mostrano a pagine, in ordine di gravità (i problemi nella prima), senza che nessuno debba scorrere.'),
}
const CI_PARAM: OpzioneEagle = { key: 'ci_param', predefinito: '', label: () => t('Parametro'), valori: [], dinamica: 'ci_param' }
/** For an interface, what the single-parameter chart draws beside (or instead of) the utilisation. */
const MISURA_GRAFICO: OpzioneEagle = {
  key: 'misura', predefinito: 'valore', label: () => t('Misura (interfacce e ping)'),
  valori: [
    { value: 'valore', label: () => t('il valore con le soglie') },
    { value: 'traffico', label: () => t('traffico in entrata e in uscita (Mbit/s)') },
    { value: 'pacchetti', label: () => t('pacchetti al secondo nei due versi') },
    { value: 'errori', label: () => t('errori e scarti nell’intervallo') },
    { value: 'rtt', label: () => t('tempo di risposta del ping (ms)') },
  ],
  aiuto: () => t('Per un’interfaccia: le serie che la sonda tiene accanto all’utilizzo. Per il ping: il tempo di risposta accanto alla perdita. Per gli altri parametri si disegna il valore.'),
}
const SERIE_GRAFICO: OpzioneEagle = {
  key: 'serie', predefinito: '', label: () => t('Serie'), valori: [], dinamica: 'serie', massimo: 8,
  aiuto: () => t('Le serie con unità diverse finiscono in pannelli separati, uno per unità: un asse solo per grafico.'),
}
const SERIE_LINK: OpzioneEagle = {
  key: 'serie', predefinito: '', label: () => t('Interfacce'), valori: [], dinamica: 'serie', soloTipi: ['interface'], massimo: 16,
  aiuto: () => t('Nell’elenco ogni porta porta la sua descrizione: è quella che diventa il titolo della scheda (si può cambiare).'),
}
const TIPO_GRAFICO: OpzioneEagle = {
  key: 'chart', predefinito: 'linea', label: () => t('Tipo di grafico'),
  valori: [
    { value: 'linea', label: () => t('linee') }, { value: 'area', label: () => t('aree') },
    { value: 'barre', label: () => t('barre') }, { value: 'impilato', label: () => t('impilato (la somma)') },
  ],
}
const SCALA_Y: OpzioneEagle = {
  key: 'yscale', predefinito: 'auto', label: () => t('Scala dell’asse Y'),
  valori: [
    { value: 'auto', label: () => t('automatica, dallo zero (le percentuali fino a 100)') },
    { value: 'adatta', label: () => t('adattata ai valori, anche senza lo zero') },
    { value: 'fissa', label: () => t('fissa: i limiti qui sotto') },
  ],
}
const Y_MIN: OpzioneEagle = { key: 'ymin', predefinito: '', tipo: 'testo', label: () => t('Limite inferiore (scala fissa)'), valori: [], aiuto: () => t('Nell’unità della misura; vuoto = zero.') }
const Y_MAX: OpzioneEagle = { key: 'ymax', predefinito: '', tipo: 'testo', label: () => t('Limite superiore (scala fissa)'), valori: [], aiuto: () => t('Nell’unità della misura; vuoto = dal valore più alto.') }

/** The Y scale from a widget's options: nothing for the default, so the chart draws as always. */
function scalaDalleOpzioni(options: Record<string, unknown>): ScalaY | undefined {
  const modo = String(options.yscale ?? 'auto')
  if (modo !== 'adatta' && modo !== 'fissa') return undefined
  const numero = (v: unknown): number | null => {
    const testo = String(v ?? '').trim().replace(',', '.')
    const n = Number(testo)
    return testo === '' || !Number.isFinite(n) ? null : n
  }
  return { modo, min: numero(options.ymin), max: numero(options.ymax) }
}

const FASCIA: OpzioneEagle = { key: 'band', predefinito: 'true', tipo: 'bool', label: () => t('Fascia minimo–massimo'), valori: [] }
const SOGLIE: OpzioneEagle = { key: 'thresholds', predefinito: 'true', tipo: 'bool', label: () => t('Soglie di attenzione e critica'), valori: [] }
const MOSTRA_LINK: OpzioneEagle = {
  key: 'mostra', predefinito: 'traffico', label: () => t('Mini-grafico'),
  valori: [{ value: 'traffico', label: () => t('traffico in e out') }, { value: 'utilizzo', label: () => t('utilizzo con le soglie') }],
}
const VISTA_TOPOLOGIA: OpzioneEagle = { key: 'view_id', predefinito: '', label: () => t('Vista'), valori: [], dinamica: 'vista_topologia' }
const ARCHI_UTILIZZO: OpzioneEagle = {
  key: 'archi', predefinito: 'true', tipo: 'bool', label: () => t('Colora gli archi per utilizzo'), valori: [],
  aiuto: () => t('Ogni arco LLDP/CDP prende lo stato peggiore delle sue due interfacce e uno spessore in proporzione all’utilizzo; le porte si riconoscono dal nome (local_port, remote_port).'),
}
const ALTEZZA_MAPPA: OpzioneEagle = {
  key: 'altezza', predefinito: 'normale', label: () => t('Altezza della tela'),
  valori: [{ value: 'normale', label: () => t('normale') }, { value: 'alta', label: () => t('alta (scorre nel riquadro)') }],
}
const CAPI_LINK: OpzioneEagle = {
  key: 'link', predefinito: '', label: () => t('I due capi'), valori: [], dinamica: 'link',
  aiuto: () => t('Il capo B si propone dall’adiacenza LLDP/CDP della porta A, ma resta tuo: la descrizione della porta è quella che conta. Senza capo B il link è a un capo solo (una WAN verso il provider).'),
}

const comeBool = (v: unknown, predefinito: boolean) => (v == null || v === '' ? predefinito : v === true || v === 'true')
const serieDalleOpzioni = (o: Record<string, unknown>): SerieScelta[] =>
  (Array.isArray(o.serie) ? (o.serie as SerieScelta[]) : []).filter((s) => s && s.ci_id && s.key)

type Props = { options: Record<string, unknown>; sinottico: boolean }

const finestra = (ore: number) => [...ORE_SERIE.valori, ...ORE_EAGLE.valori].find((v) => Number(v.value) === ore)?.label() ?? t('{n} ore', { n: ore })
const oraBreve = (iso: string) => new Date(iso).toLocaleTimeString(localeCorrente(), { hour: '2-digit', minute: '2-digit' })

function useOverview(o: Record<string, unknown>) {
  const ore = Number(o.hours ?? 24)
  return { ore, s: usePolling(() => api.eagleOverview(ore), ogniMinuto, [ore]) }
}

function Vuoto({ testo }: { testo: string }) {
  return <p className="muted" style={{ margin: 0 }}>{testo}</p>
}

// ------------------------------------------------------------------ l'inizio

function Kpi({ valore, etichetta, tono }: { valore: string | number; etichetta: string; tono: Tono }) {
  return (
    <div className={`ehero__kpi ehero__kpi--${tono}`}>
      <strong>{typeof valore === 'number' ? valore.toLocaleString(localeCorrente()) : valore}</strong>
      <span>{etichetta}</span>
    </div>
  )
}

/**
 * Come stanno le cose, in una riga: i numeri che contano, l'andamento degli
 * allarmi nella finestra e i parametri per stato. Tre cose dalla stessa
 * lettura, perché il primo riquadro del cruscotto risponda da solo a «è
 * tutto a posto?» e a «sta peggiorando?».
 */
/** The class label from the taxonomy, translated; the key when unknown. */
function etichettaClasse(tassonomia: Taxonomy | null, key: string): string {
  for (const cat of tassonomia?.categories ?? []) {
    const trovata = cat.classes.find((c) => c.key === key)
    if (trovata) return t(trovata.label)
  }
  return key === 'unknown' ? t('non classificato') : key.replace(/_/g, ' ')
}

/**
 * What is monitored, by class: «169 switch · 760 access point · 200 server»,
 * each pill in the colour of the worst status in the class. Asked for the
 * wall: the overview said 1503 ok and 159 CIs, not what those CIs were.
 */
function ClassiMonitorate({ cis }: { cis: EagleOverview['cis'] }) {
  const tassonomia = useAsync(() => api.taxonomy(), [])
  const gruppi = useMemo(() => {
    const peso: Record<string, number> = { down: 3, crit: 3, warn: 2, ok: 1 }
    const m = new Map<string, { n: number; peggio: number }>()
    for (const c of cis) {
      const k = c.ci_class || 'unknown'
      const g = m.get(k) ?? { n: 0, peggio: 0 }
      g.n += 1
      g.peggio = Math.max(g.peggio, peso[c.status] ?? 0)
      m.set(k, g)
    }
    return [...m.entries()].sort((a, b) => b[1].n - a[1].n)
  }, [cis])
  if (gruppi.length === 0) return null
  const tono = (p: number) => (p >= 3 ? 'error' : p === 2 ? 'warn' : p === 1 ? 'ok' : 'muted')
  const mostrate = gruppi.slice(0, 12)
  return (
    <div className="ehero__classi" aria-label={t('CI monitorati per classe')}>
      {mostrate.map(([k, g]) => (
        <Link key={k} to="/eagle/cis" className={`ehero__classe ehero__classe--${tono(g.peggio)}`} title={t('CI monitorati per classe')}>
          <strong>{g.n.toLocaleString(localeCorrente())}</strong> {etichettaClasse(tassonomia.data ?? null, k)}
        </Link>
      ))}
      {gruppi.length > mostrate.length && <span className="muted">{t('e altre {n} classi', { n: gruppi.length - mostrate.length })}</span>}
    </div>
  )
}

function Hero({ options }: Props) {
  const { ore, s } = useOverview(options)
  const d = s.data
  if (!d?.licensed) return <Widget titolo="Eagle" caricando={s.loading} errore={s.error}><span className="muted">—</span></Widget>
  const b = d.totals.by_status
  const gravi = b.crit + b.down
  const attesa = b.pending + b.nodata
  const parti = segmentiDaConteggi(b)
  // Le cause: le radici con impatto (e i percorsi) più le sonde che sono
  // loro il problema. Quando ci sono, è il numero che conta davvero — dieci
  // oggetti giù dietro una radice sono un guasto, non dieci.
  const radici = d.root_causes ?? []
  const sondeCausa = [...(d.probes.blind ?? []), ...(d.probes.silent ?? [])]
  const cause = radici.length + sondeCausa.length
  return (
    <Widget titolo={t('Eagle · come stanno le cose')} sotto={t('aggiornato alle {ora}', { ora: oraBreve(d.generated_at) })} caricando={s.loading} errore={s.error}
            link={<Link to="/eagle/sinottico">{t('sinottico')}</Link>}>
      <div className="ehero">
        <div className="ehero__kpi-lista">
          <Kpi valore={gravi} etichetta={t('gravi')} tono={gravi > 0 ? 'error' : 'ok'} />
          <Kpi valore={b.warn} etichetta={t('attenzione')} tono={b.warn > 0 ? 'warn' : 'muted'} />
          {cause > 0 && <Kpi valore={cause} etichetta={t('cause')} tono="error" />}
          <Kpi valore={b.ok} etichetta={t('ok')} tono="ok" />
          <Kpi valore={attesa} etichetta={t('in attesa')} tono="muted" />
          <Kpi valore={d.totals.cis} etichetta={t('CI monitorati')} tono="muted" />
          <Kpi valore={`${d.probes.alive}/${d.probes.total}`} etichetta={t('sonde')} tono={d.probes.alive < d.probes.total || sondeCausa.length > 0 ? 'warn' : 'muted'} />
        </div>
        <div className="ehero__blocco">
          <div className="ehero__etichetta">{t('parametri in allarme')} <span className="muted">· {finestra(ore)}</span></div>
          <GraficoStati punti={d.timeline} passoS={d.step_s} ore={ore} compatto altezza={54} />
        </div>
        <div className="ehero__blocco">
          <div className="ehero__etichetta">{t('{n} parametri per stato', { n: d.totals.params })}</div>
          <Segmenti parti={parti} altezza={10} />
          <div className="ehero__legenda">
            {parti.filter((p) => p.value > 0).map((p) => (
              <span key={p.key} className="gs__voce"><span className={`gs__chiave gs__chiave--${p.tone}`} />{p.value.toLocaleString(localeCorrente())} {p.label}</span>
            ))}
          </div>
        </div>
      </div>
      <ClassiMonitorate cis={d.cis} />
      {cause > 0 && (
        <div className="ehero__cause">
          <span className="ehero__etichetta">{t('cause')}:</span>
          {sondeCausa.map((v) => <VerdettoSonda key={`${v.kind}|${v.appliance_id}`} v={v} conNome />)}
          {radici.slice(0, 4).map((c) => (
            <Link key={c.ci_id} to={`/cis/${c.ci_id}#eagle`} className="ecausa ecausa--radice"
                  title={c.kind === 'path' && c.seen_by.length > 0 ? t('raggiungibile da {probes}', { probes: c.seen_by.join(', ') }) : c.services.join(', ') || undefined}>
              ⚑ {c.ci_name} · {c.kind === 'path'
                ? (c.downstream > 0 ? t('non raggiunto da {probes}, {n} dietro', { probes: c.probes.join(', '), n: c.downstream }) : t('non raggiunto da {probes}', { probes: c.probes.join(', ') }))
                : t('trascina {n} oggetti', { n: c.downstream })}
            </Link>
          ))}
          {radici.length > 4 && <span className="muted">{t('e altre {n}', { n: radici.length - 4 })}</span>}
        </div>
      )}
    </Widget>
  )
}

// ------------------------------------------------------------------ la parete

/**
 * Una tessera per CI, colorata dal suo stato peggiore, con il parametro che
 * lo ha causato e da quanto; sotto, i suoi parametri per stato in una
 * barretta — un CI «in attenzione» con un parametro su quaranta e uno con
 * venti su quaranta non sono la stessa cosa.
 */
function Parete({ options, sinottico }: Props) {
  const { s } = useOverview(options)
  const d = s.data
  const solo = String(options.only ?? 'all')
  const richiesti = tagRichiesti(options.tags)
  const ordine: Record<string, number> = { down: 0, crit: 1, warn: 2, nodata: 3, pending: 4, ok: 5 }
  const cis = [...(d?.licensed ? d.cis : [])]
    .filter((c) => haITag(c.tags, richiesti))
    .filter((c) => solo === 'all' || ['warn', 'crit', 'down'].includes(c.status))
    .sort((a, b) => (ordine[a.status] ?? 9) - (ordine[b.status] ?? 9) || a.name.localeCompare(b.name))
    // On the wall every CI has its tile (the wall scrolls); in a cockpit widget forty are enough.
    .slice(0, sinottico ? 600 : 40)
  const problemi = new Map<string, { name: string; value: number | null; unit: string; since: string | null; kind: EagleParam['kind']; key: string; cause: EagleCausa | null }>()
  for (const p of d?.licensed ? d.problems : []) if (!problemi.has(p.ci_id)) problemi.set(p.ci_id, { name: p.name, value: p.value, unit: p.unit, since: p.since, kind: p.kind, key: p.key, cause: p.cause })
  // A tile with a problem opens that parameter in a window; the others still
  // lead to the CI (there is no single parameter to show).
  const [aperto, setAperto] = useState<ParametroDaAprire | null>(null)
  // On the wall, pages: as many tiles as the box holds (measured), the
  // problems in the first page, the next page at the chosen pace. A monitor
  // has no one to scroll it.
  // On the wall the default is to page (a monitor has no one to scroll);
  // «mai» is a choice, not the absence of one.
  const ogniS = sinottico ? Number(options.pagine_s ?? 15) : 0
  // The ping response time inside each tile: one read for the whole wall,
  // only when asked (the tiles grow to make room for the graph).
  const conSpark = options.ping_spark === true || options.ping_spark === 'true'
  const spark = usePolling(() => (conSpark ? api.eagleSparklineParete('latency', 1) : Promise.resolve(null)), ogniMinuto, [conSpark])
  const sparkPer = useMemo(() => {
    const m = new Map<string, EagleSparkPunto[]>()
    for (const r of spark.data?.series ?? []) m.set(r.ci_id, r.spark)
    return m
  }, [spark.data])
  const altezzaTessera = conSpark ? 104 : 74
  const [riquadro, larghezza, altezza] = useRiquadro<HTMLDivElement>()
  const colonne = Math.max(1, Math.floor((larghezza + 6) / (150 + 6)))
  const righe = Math.max(1, Math.floor((altezza + 6) / (altezzaTessera + 6)))
  const perPagina = ogniS > 0 ? colonne * righe : Math.max(1, cis.length)
  const pagine = Math.max(1, Math.ceil(cis.length / perPagina))
  const [pagina, setPagina] = useState(0)
  useEffect(() => {
    if (ogniS <= 0 || pagine < 2) { setPagina(0); return }
    const id = window.setInterval(() => setPagina((p) => (p + 1) % pagine), ogniS * 1000)
    return () => window.clearInterval(id)
  }, [ogniS, pagine])
  const corrente = pagina % pagine
  const visibili = ogniS > 0 ? cis.slice(corrente * perPagina, (corrente + 1) * perPagina) : cis
  // «159 CI» over forty tiles was a lie: say how many are on screen.
  const totale = d?.licensed ? d.cis.filter((c) => haITag(c.tags, richiesti)).length : 0
  const sotto = solo === 'problems' ? t('solo con problemi')
    : !d?.licensed ? undefined
    : ogniS > 0 || visibili.length >= totale ? t('{n} CI', { n: totale })
    : t('{n} di {tot} CI', { n: visibili.length, tot: totale })
  return (
    <Widget titolo={t('Parete dei CI')}
            sotto={pagine > 1 ? `${sotto ?? ''} · ${t('pagina {p} di {n}', { p: corrente + 1, n: pagine })}` : sotto}
            caricando={s.loading} errore={s.error} link={<Link to="/eagle/cis">{t('apri')}</Link>}>
      {d?.licensed && (cis.length === 0 ? <Vuoto testo={solo === 'problems' ? t('tutto regolare') : richiesti.length > 0 ? t('Nessun CI con questi tag.') : t('Nessun CI monitorato.')} /> : (
        <div ref={riquadro} className={`esin__parete esin__parete--widget${ogniS > 0 ? ' esin__parete--pagine' : ''}`} style={{ gridAutoRows: altezzaTessera }}>
          {visibili.map((c) => {
            const p = problemi.get(c.ci_id)
            const classe = `esin__tessera esin__tessera--${tonoStato(c.status)} esin__tessera--piccola`
            const dentro = (
              <>
                <div className="esin__tessera-nome">{c.name}</div>
                <div className="esin__tessera-motivo">
                  {p ? <>{etichettaParametro(p)}{p.value != null && <strong> {formatConUnita(p.value, p.unit)}</strong>}<span className="esin__tessera-da"> · {timeAgo(p.since)}</span></> : etichettaStato(c.status)}
                </div>
                {p?.cause && <div className="esin__tessera-causa"><Causa c={p.cause} link={false} /></div>}
                {conSpark && (sparkPer.get(c.ci_id)?.filter((x) => x.value != null).length ?? 0) >= 2 && (
                  <div className="esin__tessera-spark">
                    <Sparkline punti={sparkPer.get(c.ci_id) ?? []} ore={1} unit="ms" altezza={24} tono={tonoStato(c.status)} nome={`${c.name} · ping`} />
                  </div>
                )}
                <div className="esin__tessera-conteggi"><Segmenti parti={segmentiDaConteggi(c.counts)} altezza={4} /></div>
              </>
            )
            return p ? (
              <button key={c.ci_id} type="button" className={`${classe} esin__tessera--bottone`} title={t('Apri il grafico del parametro')}
                      onClick={() => setAperto({ ci_id: c.ci_id, ci_name: c.name, key: p.key, name: p.name, kind: p.kind, unit: p.unit })}>
                {dentro}
              </button>
            ) : (
              <Link key={c.ci_id} to={`/cis/${c.ci_id}#eagle`} className={classe}>{dentro}</Link>
            )
          })}
        </div>
      ))}
      {aperto && <FinestraParametro scelto={aperto} onChiudi={() => setAperto(null)} />}
    </Widget>
  )
}

// ------------------------------------------------------ lo storico degli allarmi

/** Stacked bars, one per interval: how many episodes began in it, by state. */
function BarreEpisodi({ buckets, passoS }: { buckets: EagleStoricoAllarmi['buckets']; passoS: number }) {
  const [ref, larghezza] = useLarghezza<HTMLDivElement>(320)
  if (!buckets.length) return null
  const W = Math.max(160, larghezza)
  const H = 46
  const basso = 14
  const massimo = Math.max(1, ...buckets.map((b) => b.warn + b.crit + b.down))
  const lw = W / buckets.length
  const giorno = passoS >= 86400
  const etichetta = (iso: string) => (giorno
    ? new Date(iso).toLocaleDateString(localeCorrente(), { day: '2-digit', month: '2-digit' })
    : formatOra(new Date(iso).getTime()))
  const ogni = Math.max(1, Math.ceil(buckets.length / 6))
  return (
    <div className="estorico__barre" ref={ref}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t('Episodi di allarme per intervallo')}>
        <line x1={0} x2={W} y1={H - basso} y2={H - basso} className="estorico__base" />
        {buckets.map((b, i) => {
          const totale = b.warn + b.crit + b.down
          const x = i * lw + 1
          const w = Math.max(1, lw - 2)
          let y = H - basso
          const segmenti: { k: string; y: number; h: number }[] = []
          for (const [k, n] of [['down', b.down], ['crit', b.crit], ['warn', b.warn]] as [string, number][]) {
            if (!n) continue
            const h = ((H - basso - 2) * n) / massimo
            y -= h
            segmenti.push({ k, y, h })
          }
          return (
            <g key={b.ts}>
              <title>{`${etichetta(b.ts)} · ${t('{n} episodi', { n: totale })}`}</title>
              {segmenti.map((sg) => <rect key={sg.k} x={x} y={sg.y} width={w} height={sg.h} className={`estorico__barra estorico__barra--${sg.k}`} />)}
              {i % ogni === 0 && <text x={x} y={H - 2} className="estorico__etichetta">{etichetta(b.ts)}</text>}
            </g>
          )
        })}
      </svg>
    </div>
  )
}

/**
 * The alarm episodes of the period, the ones already over included: the
 * 3-minute down at four in the morning is back by eight, is not among the
 * open problems, and in the timeline is a bump nobody can read. Here: which
 * CI, which parameter, from when to when, how long; a click opens the chart
 * in a window.
 */
function StoricoAllarmi({ options, sinottico }: Props) {
  const ore = Number(options.hours ?? 24)
  const filtro = String(options.stati ?? 'tutti')
  const s = usePolling(() => api.eagleStoricoAllarmi(ore, 200), ogniMinuto, [ore])
  const d = s.data?.licensed ? s.data : null
  const episodi = (d?.episodes ?? []).filter((e) => (filtro === 'giu' ? e.status === 'down' : filtro === 'gravi' ? e.status !== 'warn' : true))
  const [aperto, setAperto] = useState<ParametroDaAprire | null>(null)
  const righe = episodi.slice(0, sinottico ? 14 : 10)
  return (
    <Widget titolo={t('Storico degli allarmi')}
            sotto={d ? `${finestra(ore)} · ${t('{n} episodi', { n: d.total })}` : undefined}
            caricando={s.loading} errore={s.error} link={<Link to="/eagle/report">{t('report')}</Link>}>
      {d && (d.total === 0 ? <Vuoto testo={t('Nessun allarme nel periodo.')} /> : (
        <>
          <BarreEpisodi buckets={d.buckets} passoS={d.step_s} />
          {righe.length === 0 ? <Vuoto testo={t('Nessun episodio con questo filtro.')} /> : (
            <ul className="estorico">
              {righe.map((e) => (
                <li key={`${e.ci_id}|${e.key}|${e.from}`} className={`estorico__riga estorico__riga--${tonoStato(e.status)}`}>
                  <button type="button" className="linklike estorico__chi" title={t('Apri il grafico del parametro')}
                          onClick={() => setAperto({ ci_id: e.ci_id, ci_name: e.ci_name, key: e.key, name: e.name, kind: e.kind, unit: e.unit })}>
                    <PuntoStato stato={e.status} /> <strong>{e.ci_name}</strong>
                  </button>
                  <span className="muted estorico__cosa">{etichettaParametro(e)}{e.value != null && <span className="mono"> {formatConUnita(e.value, e.unit)}</span>}</span>
                  <span className="mono estorico__quando">
                    {formatIstante(new Date(e.from).getTime())} – {e.open ? t('in corso') : formatOra(new Date(e.to).getTime())}
                    <span className="muted"> · {durataBreve(e.duration_s)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {episodi.length > righe.length && (
            <p className="muted eagle-piccolo" style={{ margin: '6px 0 0' }}>{t('e altri {n} episodi nel periodo', { n: episodi.length - righe.length })}</p>
          )}
        </>
      ))}
      {aperto && <FinestraParametro scelto={aperto} ore={ore} onChiudi={() => setAperto(null)} />}
    </Widget>
  )
}

// ------------------------------------------------------ il grafico a scelta

function Grafico({ options }: Props) {
  const ciId = String(options.ci_id ?? '')
  const key = String(options.key ?? '')
  const ore = Number(options.hours ?? 24)
  const [param, setParam] = useState<EagleParam | null>(null)
  useEffect(() => {
    if (!ciId || !key) { setParam(null); return }
    let vivo = true
    void api.ciEagle(ciId).then((d) => { if (vivo) setParam((d.params ?? []).find((p) => p.key === key) ?? null) }).catch(() => undefined)
    return () => { vivo = false }
  }, [ciId, key])
  const serie = usePolling(() => (ciId && key ? api.ciEagleSerie(ciId, key, ore) : Promise.resolve(null)), ogniMinuto, [ciId, key, ore])
  // For an interface: the traffic in Mbit/s (or packets, or errors) instead
  // of the utilisation — the question «how many Mbit/s on this port» was not
  // answerable from the dashboard before.
  const misura = param?.kind === 'interface' || param?.kind === 'ping' ? String(options.misura ?? 'valore') : 'valore'
  const d = serie.data?.derived ?? {}
  const coppia = (a: 'in' | 'pkts_in' | 'errors', b: 'out' | 'pkts_out' | 'discards', na: string, nb: string): SerieGrafico[] =>
    [{ id: a, nome: na, punti: d[a] ?? [] }, { id: b, nome: nb, punti: d[b] ?? [] }].filter((x) => x.punti.length > 0)
  const derivate: { serie: SerieGrafico[]; unit: string; nome: string } | null =
    misura === 'traffico' ? { serie: coppia('in', 'out', t('Entrata'), t('Uscita')), unit: 'Mbit/s', nome: t('Traffico') }
    : misura === 'pacchetti' ? { serie: coppia('pkts_in', 'pkts_out', t('Entrata'), t('Uscita')), unit: 'pkt/s', nome: t('Pacchetti al secondo') }
    : misura === 'errori' ? { serie: coppia('errors', 'discards', t('Errori'), t('Scarti')), unit: '', nome: t('Errori e scarti') }
    : misura === 'rtt' ? { serie: [{ id: 'rtt', nome: t('Tempo di risposta'), punti: d.rtt ?? [] }].filter((x) => x.punti.length > 0), unit: 'ms', nome: t('Tempo di risposta') }
    : null
  const titolo = param ? `${String(options.ci_name ?? '')} · ${etichettaParametro(param)}${derivate ? ` · ${derivate.nome}` : ''}` : t('Grafico di un parametro')
  return (
    <Widget titolo={titolo} sotto={param ? finestra(ore) : undefined} caricando={serie.loading} errore={serie.error}
            link={ciId ? <Link to={`/cis/${ciId}#eagle`}>{t('apri')}</Link> : undefined}
            azioni={param?.status ? <><StatoBadge stato={param.status} /> {param.last_value != null && <span className="mono">{formatConUnita(param.last_value, param.unit)}</span>}</> : undefined}>
      {!ciId || !key ? <Vuoto testo={t('Scegli un CI e un parametro in «Personalizza».')} />
        : param && serie.data ? (
          derivate ? (
            derivate.serie.length === 0
              ? <Vuoto testo={t('La sonda non ha ancora queste serie per l’interfaccia (arrivano con le finestre successive, o l’apparato non espone i contatori).')} />
              : <GraficoSerie punti={[]} serieEsplicite={derivate.serie} ore={ore} altezza={190} fascia={false} stato={false} scalaY={scalaDalleOpzioni(options)}
                              param={{ unit: derivate.unit, warn: null, crit: null, direction: 'above', name: derivate.nome, kind: 'interface' }} />
          ) : <GraficoSerie punti={serie.data.points} param={param} ore={ore} altezza={190} scalaY={scalaDalleOpzioni(options)} />
        ) : null}
    </Widget>
  )
}

// ---------------------------------------------- la latenza verso un indirizzo

const BERSAGLIO: OpzioneEagle = {
  key: 'bersaglio', predefinito: '', valori: [], dinamica: 'bersaglio',
  label: () => t('Indirizzo e sonda'),
}

/**
 * «Latenza verso un indirizzo»: the round-trip time to a public address
 * (8.8.8.8, the far end of a WAN) as measured by a probe of your choice —
 * «how long to reach the Internet from this room». The target is a CI made
 * for the address (see `eagle_target_service`), with the fast ping on; the
 * drawing is the ordinary parameter graph on `ping_fast`.
 */
/** One parameter of one CI, big: the value in the colour of its status, or
 *  the status with the value under it; the last hour as a sparkline. */
function Valore({ options }: Props) {
  const ciId = String(options.ci_id ?? '')
  const key = String(options.key ?? '')
  const modo = String(options.show ?? 'valore')
  const conSpark = comeBool(options.spark, true)
  const ci = usePolling(() => (ciId ? api.ciEagle(ciId) : Promise.resolve(null)), ogniMinuto, [ciId])
  const param = (ci.data?.params ?? []).find((p) => p.key === key) ?? null
  const serie = usePolling(() => (conSpark && ciId && key ? api.ciEagleSerie(ciId, key, 1) : Promise.resolve(null)), ogniMinuto, [conSpark, ciId, key])
  const stato = param?.status ?? 'pending'
  const tono = tonoStato(stato)
  const titolo = param ? `${String(options.ci_name ?? '')} · ${etichettaParametro(param)}` : t('Valore di un parametro')
  const valore = param?.last_value != null ? formatConUnita(param.last_value, param.unit) : '—'
  const soglie = param && (param.warn != null || param.crit != null)
    ? [param.warn != null ? `${t('attenzione')} ${param.direction === 'below' ? '<' : '>'} ${formatConUnita(param.warn, param.unit)}` : null,
       param.crit != null ? `${t('critico')} ${param.direction === 'below' ? '<' : '>'} ${formatConUnita(param.crit, param.unit)}` : null].filter(Boolean).join(' · ')
    : null
  return (
    <Widget titolo={titolo} sotto={param?.last_at ? t('letto {q}', { q: timeAgo(param.last_at) }) : undefined}
            caricando={ci.loading && !ci.data} errore={ci.error} link={ciId ? <Link to={`/cis/${ciId}#eagle`}>{t('apri')}</Link> : undefined}>
      {!ciId || !key ? <Vuoto testo={t('Scegli CI e parametro in «Titolo e opzioni».')} /> : !param && ci.data ? <Vuoto testo={t('Parametro non più presente sul CI.')} /> : param && (
        <div className={`esin__tessera esin__tessera--${tono} evalore`} title={param.last_error ?? undefined}>
          {modo === 'stato' ? (
            <>
              <div className="evalore__grande">{etichettaStato(stato)}</div>
              <div className="evalore__sotto mono">{valore}</div>
            </>
          ) : (
            <>
              <div className="evalore__grande mono">{valore}</div>
              <div className="evalore__sotto">{etichettaStato(stato)}{param.status_since ? ` · ${t('da {q}', { q: timeAgo(param.status_since) })}` : ''}</div>
            </>
          )}
          {soglie && <div className="evalore__soglie">{soglie}</div>}
          {conSpark && (serie.data?.points?.filter((x) => x.value != null).length ?? 0) >= 2 && (
            <div className="evalore__spark">
              <Sparkline punti={serie.data!.points.map((x) => ({ ts: x.ts, value: x.value, min: x.min ?? null, max: x.max ?? null }))} ore={1} unit={param.unit} altezza={28} tono={tono} nome={titolo} />
            </div>
          )}
        </div>
      )}
    </Widget>
  )
}

function Latenza({ options, sinottico }: Props) {
  const ciId = String(options.ci_id ?? '')
  if (!ciId) {
    return (
      <Widget titolo={t('Latenza verso un indirizzo')}>
        <Vuoto testo={t('Scegli l’indirizzo e la sonda in «Personalizza», poi «Collega».')} />
      </Widget>
    )
  }
  const nome = String(options.ci_name ?? options.address ?? '')
  const sonda = String(options.probe_name ?? '')
  return <Grafico options={{ ...options, key: 'ping_fast', ci_name: sonda ? `${nome} · ${t('da {probe}', { probe: sonda })}` : nome }} sinottico={sinottico} />
}

// ------------------------------------------------- il grafico personalizzato

/** Le serie chieste che il Control Plane non ha restituito (parametro spento, CI tolto). */
function nonTrovate(scelte: SerieScelta[], risposte: EagleSerieMultipla[]): SerieScelta[] {
  return scelte.filter((s) => !risposte.some((r) => r.ci_id === s.ci_id && r.key === s.key))
}

function useSerieMultiple(scelte: SerieScelta[], ore: number) {
  const firma = JSON.stringify(scelte.map((s) => [s.ci_id, s.key]))
  const coppie = useMemo(() => scelte.map((s) => ({ ci_id: s.ci_id, key: s.key })), [firma]) // eslint-disable-line react-hooks/exhaustive-deps
  return usePolling(() => (coppie.length ? api.eagleSerieMultiple(coppie, ore) : Promise.resolve(null)), ogniMinuto, [firma, ore])
}

/**
 * Le serie che la persona sceglie — anche di CI diversi — in un grafico
 * solo: linee, aree, barre o impilate (la somma del traffico delle WAN).
 * Un asse solo: serie con unità diverse vanno in pannelli separati, uno per
 * unità. Le soglie si disegnano quando sono le stesse per tutte le serie
 * del pannello; la striscia di stato solo con una serie.
 */
function GraficoLibero({ options }: Props) {
  const scelte = serieDalleOpzioni(options)
  const ore = Number(options.hours ?? 24)
  const tipo = (['linea', 'area', 'barre', 'impilato'].includes(String(options.chart)) ? String(options.chart) : 'linea') as TipoGrafico
  const fascia = comeBool(options.band, true)
  const soglie = comeBool(options.thresholds, true)
  const s = useSerieMultiple(scelte, ore)
  const risposte = s.data?.licensed ? s.data.series : []
  const pannelli = useMemo(() => {
    const per = new Map<string, { serie: SerieGrafico[]; warn: number | null; crit: number | null; kind: EagleParam['kind'] }>()
    for (const r of risposte) {
      const scelta = scelte.find((x) => x.ci_id === r.ci_id && x.key === r.key)
      // An interface can be drawn by one of its derived series (traffic in
      // Mbit/s, packets, errors): its own unit, no thresholds.
      const misura = r.kind === 'interface' && scelta?.misura && scelta.misura !== 'valore' ? scelta.misura : undefined
      const unit = unitaDiMisura(misura, r.unit)
      const punti = misura ? (r.derived?.[misura] ?? []) : r.points
      const warn = misura ? null : r.warn
      const crit = misura ? null : r.crit
      const voce = per.get(unit) ?? { serie: [], warn, crit, kind: r.kind }
      voce.serie.push({ id: `${r.ci_id}|${r.key}|${misura ?? 'valore'}`, nome: scelta?.label?.trim() || `${r.ci_name} · ${r.name}`, punti })
      // Le soglie restano solo se tutte le serie del pannello hanno le stesse.
      if (voce.warn !== warn) voce.warn = null
      if (voce.crit !== crit) voce.crit = null
      per.set(unit, voce)
    }
    return [...per.entries()]
  }, [risposte, scelte])
  const mancanti = nonTrovate(scelte, risposte)
  return (
    <Widget titolo={t('Grafico personalizzato')} sotto={scelte.length ? finestra(ore) : undefined} caricando={s.loading} errore={s.error}>
      {scelte.length === 0 ? <Vuoto testo={t('Scegli le serie in «Titolo e opzioni» (in modifica).')} /> : s.data && (
        <>
          {pannelli.map(([unit, p]) => (
            <div key={unit || '-'} className="glibero__pannello">
              {pannelli.length > 1 && <div className="glibero__unita">{unit || t('conteggi')}</div>}
              <GraficoSerie punti={[]} serieEsplicite={p.serie} ore={ore} tipo={tipo} fascia={fascia} scalaY={scalaDalleOpzioni(options)}
                            soglie={soglie && p.warn != null} stato={p.serie.length === 1}
                            altezza={pannelli.length > 1 ? 150 : 210}
                            param={{ unit, warn: p.warn, crit: p.crit, direction: 'above', name: t('Grafico personalizzato'), kind: p.kind }} />
            </div>
          ))}
          {mancanti.length > 0 && (
            <p className="muted eagle-piccolo" style={{ margin: '6px 0 0' }}>
              {t('{n} serie non sono più monitorate: {elenco}', { n: mancanti.length, elenco: mancanti.map((m) => m.label || `${m.ci_name} · ${m.key}`).join(', ') })}
            </p>
          )}
        </>
      )}
    </Widget>
  )
}

// ------------------------------------------------------- la parete dei link

const ultimo = (punti: EaglePunto[] | undefined) => {
  const lista = (punti ?? []).filter((p) => p.value != null)
  return lista.length ? (lista[lista.length - 1].value as number) : null
}
const somma = (punti: EaglePunto[] | undefined) => (punti ?? []).reduce((a, p) => a + (p.value ?? 0) * Math.max(1, p.count ?? 1), 0)
const mbit = (v: number | null) => formatConUnita(v, 'Mbit/s')

/**
 * Una scheda per interfaccia — le dieci WAN, le porte fra i data center —
 * tutte sott'occhio: la descrizione della porta come titolo, lo stato, il
 * traffico in entrata e in uscita adesso, l'utilizzo con le tacche delle
 * soglie, e il mini-grafico delle ultime ore (traffico in e out, oppure
 * l'utilizzo con le soglie). Errori e scarti compaiono solo se ci sono.
 */
function PareteLink({ options }: Props) {
  const scelte = serieDalleOpzioni(options)
  const ore = Number(options.hours ?? 6)
  const mostra = String(options.mostra ?? 'traffico')
  const s = useSerieMultiple(scelte, ore)
  const risposte = s.data?.licensed ? s.data.series : []
  const mancanti = nonTrovate(scelte, risposte)
  return (
    <Widget titolo={t('Parete dei link')} sotto={scelte.length ? t('{n} interfacce · {f}', { n: scelte.length, f: finestra(ore) }) : undefined}
            caricando={s.loading} errore={s.error}>
      {scelte.length === 0 ? <Vuoto testo={t('Scegli le interfacce in «Titolo e opzioni» (in modifica): ogni porta mostra la sua descrizione.')} /> : s.data && (
        <>
          <div className="plink">
            {scelte.map((sc) => {
              const r = risposte.find((x) => x.ci_id === sc.ci_id && x.key === sc.key)
              if (!r) return null
              const tono = tonoStato(r.status)
              const d = r.derived ?? {}
              const entrata = ultimo(d.in)
              const uscita = ultimo(d.out)
              const errori = somma(d.errors)
              const scarti = somma(d.discards)
              const titolo = sc.label?.trim() || `${r.ci_name} · ${r.name}`
              const traffico: SerieGrafico[] = [
                { id: 'in', nome: t('Entrata'), punti: d.in ?? [] }, { id: 'out', nome: t('Uscita'), punti: d.out ?? [] },
              ].filter((x) => x.punti.length > 0)
              return (
                <Link key={`${sc.ci_id}|${sc.key}`} to={`/cis/${r.ci_id}#eagle`} className={`plink__scheda plink__scheda--${tono}`}>
                  <div className="plink__testa">
                    <PuntoStato stato={r.status} />
                    <span className="plink__titolo" title={titolo}>{titolo}</span>
                    <span className="plink__ci muted">{r.ci_name}</span>
                  </div>
                  <div className="plink__numeri">
                    <span title={t('Entrata')}>↓ <strong className="mono">{mbit(entrata)}</strong></span>
                    <span title={t('Uscita')}>↑ <strong className="mono">{mbit(uscita)}</strong></span>
                    {r.unit === '%' && r.last_value != null && <span title={t('Utilizzo')}><strong className={`mono eriga__valore--${tono}`}>{formatValore(r.last_value)} %</strong></span>}
                  </div>
                  {r.unit === '%' && r.last_value != null && (
                    <BarraSoglie valore={r.last_value} massimo={100} warn={r.warn} crit={r.crit} tono={tono} unit="%" altezza={6} />
                  )}
                  <div className="plink__grafico">
                    {mostra === 'utilizzo' || traffico.length === 0
                      ? <GraficoSerie punti={r.points} param={{ unit: r.unit, warn: r.warn, crit: r.crit, direction: r.direction, name: titolo, kind: r.kind }} ore={ore} compatto altezza={56} />
                      : <GraficoSerie punti={[]} serieEsplicite={traffico} ore={ore} compatto altezza={56} fascia={false}
                                      param={{ unit: 'Mbit/s', warn: null, crit: null, direction: 'above', name: titolo, kind: 'interface' }} />}
                  </div>
                  {(errori > 0 || scarti > 0) && (
                    <div className="plink__nota eagle-motivo">{t('{e} errori · {s} scarti nella finestra', { e: errori.toLocaleString(localeCorrente()), s: scarti.toLocaleString(localeCorrente()) })}</div>
                  )}
                </Link>
              )
            })}
          </div>
          {mancanti.length > 0 && (
            <p className="muted eagle-piccolo" style={{ margin: '6px 0 0' }}>
              {t('{n} interfacce non sono più monitorate: {elenco}', { n: mancanti.length, elenco: mancanti.map((m) => m.label || `${m.ci_name} · ${m.key}`).join(', ') })}
            </p>
          )}
        </>
      )}
    </Widget>
  )
}

// ------------------------------------------------------------------ il link

function Capo({ nome, serie, risposta, lato }: { nome: string; serie: SerieScelta | null; risposta?: EagleSerieMultipla; lato: 'a' | 'b' }) {
  if (!serie) {
    return (
      <div className={`elink__capo elink__capo--${lato} elink__capo--vuoto`}>
        <strong>{nome || t('altro capo')}</strong>
        <span className="muted">{t('non monitorato da Eagle')}</span>
      </div>
    )
  }
  const tono = tonoStato(risposta?.status)
  // The label is «CI · porta · descrizione»: the CI is already the title of
  // the card (or sits next to the port when the card has a name of its own),
  // the port stays on one line, the description — «to WDM HUAWEI … -> SWITCH
  // …», the part that says what the link is — may take up to three lines
  // instead of being cut after a few words.
  const [ci, porta, ...resto] = (serie.label || `${serie.ci_name} · ${serie.key}`).split(' · ')
  const descrizione = resto.join(' · ')
  return (
    <Link to={`/cis/${serie.ci_id}#eagle`} className={`elink__capo elink__capo--${lato} elink__capo--${tono}`}>
      <span className="elink__nome"><PuntoStato stato={risposta?.status} /><strong>{nome || serie.ci_name}</strong></span>
      <span className="elink__porta muted" title={serie.label}>{porta ?? ci}{nome && ci !== nome ? ` · ${ci}` : ''}</span>
      {descrizione && <span className="elink__desc" title={descrizione}>{descrizione}</span>}
      {risposta?.unit === '%' && risposta.last_value != null ? (
        <>
          <BarraSoglie valore={risposta.last_value} massimo={100} warn={risposta.warn} crit={risposta.crit} tono={tono} unit="%" altezza={6} />
          <span className={`elink__util mono eriga__valore--${tono}`}>{formatValore(risposta.last_value)} % {t('di utilizzo')}</span>
        </>
      ) : risposta ? <span className="elink__util muted">{etichettaStato(risposta.status)}</span> : <span className="elink__util muted">{t('non ancora')}</span>}
    </Link>
  )
}

/**
 * Un link fra due interfacce — o verso un capo non monitorato (la WAN verso
 * il provider). Il tubo porta i due versi: A→B è l'uscita di A, B→A la sua
 * entrata (quando c'è B si può confrontare con quello che B vede). Il tubo
 * prende il colore dello stato peggiore dei due capi; sotto, il
 * mini-grafico del traffico nei due versi e, se ci sono, errori e scarti.
 */
function WidgetLink({ options }: Props) {
  const v = options.link
  const link: LinkScelto = v && typeof v === 'object' ? { ...LINK_VUOTO, ...(v as Partial<LinkScelto>) } : LINK_VUOTO
  const ore = Number(options.hours ?? 6)
  const scelte = [link.a, link.b].filter((s): s is SerieScelta => !!s && !!s.ci_id && !!s.key)
  const s = useSerieMultiple(scelte, ore)
  const risposte = s.data?.licensed ? s.data.series : []
  const ra = link.a ? risposte.find((r) => r.ci_id === link.a!.ci_id && r.key === link.a!.key) : undefined
  const rb = link.b ? risposte.find((r) => r.ci_id === link.b!.ci_id && r.key === link.b!.key) : undefined
  const da = ra?.derived ?? {}
  const versoB = ultimo(da.out)
  const versoA = ultimo(da.in)
  const errori = somma(da.errors) + somma(rb?.derived?.errors)
  const scarti = somma(da.discards) + somma(rb?.derived?.discards)
  const peggiore = [ra?.status, rb?.status].reduce<string | null>((acc, st) => {
    const ordine: Record<string, number> = { down: 4, crit: 3, warn: 2, ok: 1 }
    return (ordine[st ?? ''] ?? 0) > (ordine[acc ?? ''] ?? 0) ? (st ?? null) : acc
  }, null)
  const tono = tonoStato(peggiore)
  const traffico: SerieGrafico[] = [
    { id: 'out', nome: t('A → B'), punti: da.out ?? [] }, { id: 'in', nome: t('B → A'), punti: da.in ?? [] },
  ].filter((x) => x.punti.length > 0)
  const nomeA = link.label_a || link.a?.ci_name || 'A'
  const nomeB = link.label_b || link.b?.ci_name || t('altro capo')
  // The chart takes all the room the widget leaves under the two ends: a
  // tall widget showed a 64px strip over a hundred empty pixels.
  const [rifGrafico, altezzaGrafico] = useAltezza<HTMLDivElement>(120)
  return (
    <Widget titolo={t('Link')} sotto={link.a ? `${nomeA} ↔ ${nomeB} · ${finestra(ore)}` : undefined} caricando={s.loading} errore={s.error}>
      {!link.a ? <Vuoto testo={t('Scegli i due capi in «Titolo e opzioni» (in modifica).')} /> : s.data && (
        <div className="elink__tutto">
          <div className={`elink elink--${tono}`}>
            <Capo nome={link.label_a} serie={link.a} risposta={ra} lato="a" />
            <div className="elink__tubo" role="img" aria-label={t('{a} verso {b}: {ab}; {b} verso {a}: {ba}', { a: nomeA, b: nomeB, ab: mbit(versoB), ba: mbit(versoA) })}>
              <div className="elink__verso elink__verso--ab"><span className="elink__freccia">▶</span><span className="mono">{mbit(versoB)}</span></div>
              <div className="elink__verso elink__verso--ba"><span className="elink__freccia">◀</span><span className="mono">{mbit(versoA)}</span></div>
              {peggiore && <span className={`badge badge--${tono} elink__stato`}>{etichettaStato(peggiore)}</span>}
            </div>
            <Capo nome={link.label_b} serie={link.b} risposta={rb} lato="b" />
          </div>
          {traffico.length > 0 && (
            <div className="elink__grafico" ref={rifGrafico}>
              {altezzaGrafico >= 110
                ? <GraficoSerie punti={[]} serieEsplicite={traffico} ore={ore} fascia={false} soglie={false} stato={false} altezza={altezzaGrafico}
                                param={{ unit: 'Mbit/s', warn: null, crit: null, direction: 'above', name: `${nomeA} ↔ ${nomeB}`, kind: 'interface' }} />
                : <GraficoSerie punti={[]} serieEsplicite={traffico} ore={ore} compatto fascia={false} altezza={Math.max(56, altezzaGrafico)}
                                param={{ unit: 'Mbit/s', warn: null, crit: null, direction: 'above', name: `${nomeA} ↔ ${nomeB}`, kind: 'interface' }} />}
            </div>
          )}
          {(errori > 0 || scarti > 0) && (
            <div className="plink__nota eagle-motivo">{t('{e} errori · {s} scarti nella finestra', { e: errori.toLocaleString(localeCorrente()), s: scarti.toLocaleString(localeCorrente()) })}</div>
          )}
          {nonTrovate(scelte, risposte).length > 0 && (
            <p className="muted eagle-piccolo" style={{ margin: '6px 0 0' }}>{t('Un capo non è più monitorato: controlla le interfacce in «Titolo e opzioni».')}</p>
          )}
        </div>
      )}
    </Widget>
  )
}

// --------------------------------------------------------- le classifiche

/**
 * I parametri di un tipo con il valore più alto adesso: per ognuno il valore,
 * la barra sulla scala comune con le tacche delle soglie — quanto manca alla
 * soglia si vede senza leggere — e la sparkline delle ultime ore, letta per
 * tutte le righe in una sola volta dal Control Plane.
 */
function Top({ options, sinottico }: Props) {
  const kind = String(options.kind ?? 'cpu')
  const ore = Number(options.hours ?? 6)
  const s = usePolling(() => api.eagleTop(kind, sinottico ? 12 : 8, ore), ogniMinuto, [kind, sinottico, ore])
  const righe = (s.data?.rows ?? []).filter((r) => r.value != null)
  const unit = righe[0]?.unit ?? ''
  const valori = righe.flatMap((r) => [r.value as number, r.warn ?? 0, r.crit ?? 0])
  const massimo = unit === '%' ? 100 : niceMax(Math.max(1, ...valori))
  const etichetta = TIPO_TOP.valori.find((v) => v.value === kind)?.label() ?? kind
  return (
    <Widget titolo={t('{tipo} più alti', { tipo: etichetta })} sotto={t('valore attuale e ultime {ore} ore', { ore })} caricando={s.loading} errore={s.error}
            link={<Link to="/eagle/cis">{t('apri')}</Link>}>
      {s.data && (righe.length === 0 ? <Vuoto testo={t('Nessuna misura ancora.')} /> : (
        <ul className="eriga-lista">
          {righe.map((r) => {
            const tono = tonoStato(r.status)
            return (
              <li key={`${r.ci_id}|${r.key}`} className="eriga">
                <span className="eriga__nome">
                  <PuntoStato stato={r.status} /><Link to={`/cis/${r.ci_id}#eagle`}>{r.ci_name}</Link>
                  {r.name !== r.ci_name && <span className="eriga__sotto"> · {r.name}</span>}
                </span>
                <span className={`eriga__valore mono eriga__valore--${tono}`}>{formatConUnita(r.value as number, r.unit)}</span>
                <span className="eriga__barra"><BarraSoglie valore={r.value as number} massimo={massimo} warn={r.warn} crit={r.crit} tono={tono} unit={r.unit} /></span>
                <span className="eriga__spark">
                  <Sparkline punti={r.spark ?? []} ore={ore} unit={r.unit} warn={r.warn} crit={r.crit} tono={tono} nome={`${r.ci_name} · ${r.name}`} altezza={26} />
                </span>
              </li>
            )
          })}
        </ul>
      ))}
    </Widget>
  )
}

// ------------------------------------------------ disponibilità e incidenti

function useDisponibilita(o: Record<string, unknown>) {
  const ore = Number(o.hours ?? 24)
  return { ore, s: usePolling(() => api.eagleAvailability(ore), 5 * ogniMinuto, [ore]) }
}

/** Il periodo che il Control Plane misura davvero per quella finestra (24 ore, 7 o 30 giorni). */
const periodoDisponibilita = (ore: number) => (ore <= 24 ? 24 : ore <= 168 ? 168 : 720)

/**
 * I CI meno disponibili nel periodo. La barra è sulla scala che conta — da
 * 90 (o dal minimo visto, se più basso) a 100 % — perché fra 99,2 e 99,9 c'è
 * la differenza fra uno SLA rispettato e uno mancato, e su una scala da zero
 * non si vedrebbe. La tacca è il traguardo dello SLA del CI.
 */
function Disponibilita({ options, sinottico }: Props) {
  const { ore, s } = useDisponibilita(options)
  const righe = (s.data?.licensed ? s.data.cis : []).filter((c) => c.availability_pct != null).slice(0, sinottico ? 12 : 8)
  const minimo = Math.min(100, ...righe.map((c) => c.availability_pct as number))
  const base = Math.max(0, Math.min(90, Math.floor(minimo)))
  return (
    <Widget titolo={t('Disponibilità più bassa')} sotto={finestra(periodoDisponibilita(ore))} caricando={s.loading} errore={s.error}
            link={<Link to="/eagle/report">{t('report')}</Link>}>
      {s.data && (righe.length === 0 ? <Vuoto testo={t('Nessuna misura nel periodo.')} /> : (
        <>
          <ul className="eriga-lista">
            {righe.map((c) => {
              const pct = c.availability_pct as number
              const tono: Tono = c.sla_met === false ? 'error' : pct < 99 ? 'warn' : 'ok'
              const testo = `${pct.toLocaleString(localeCorrente(), { maximumFractionDigits: 2 })} %`
              return (
                <li key={c.ci_id} className="eriga eriga--tre">
                  <span className="eriga__nome">
                    <Link to={`/cis/${c.ci_id}#eagle`}>{c.ci_name}</Link>
                    {c.sla_name && <span className="eriga__sotto"> · {c.sla_name} {c.sla_target} %</span>}
                  </span>
                  <span className={`eriga__valore mono eriga__valore--${tono}`}>{testo}</span>
                  <span className="eriga__barra">
                    <BarraSoglie valore={pct - base} massimo={100 - base} offset={base} traguardo={c.sla_target != null ? c.sla_target - base : null} tono={tono} unit="%"
                                 testo={c.sla_target != null ? t('{v} di disponibilità, traguardo {s} %', { v: testo, s: c.sla_target }) : t('{v} di disponibilità', { v: testo })} />
                  </span>
                </li>
              )
            })}
          </ul>
          <div className="eriga-nota muted">{t('scala da {base} a 100 %; la tacca è il traguardo dello SLA', { base })}</div>
        </>
      ))}
    </Widget>
  )
}

/** Gli incidenti sul filo del tempo, un rigo per CI: quando e per quanto. */
function Incidenti({ options, sinottico }: Props) {
  const { ore, s } = useDisponibilita(options)
  const periodo = periodoDisponibilita(ore)
  const incidenti = s.data?.licensed ? s.data.incidents : []
  const totale = incidenti.reduce((a, i) => a + i.duration_s, 0)
  return (
    <Widget titolo={t('Incidenti recenti')} sotto={finestra(periodo)} caricando={s.loading} errore={s.error}
            azioni={incidenti.length > 0 ? <span className="muted">{t('{n} incidenti · {d} in totale', { n: incidenti.length, d: durataBreve(totale) })}</span> : undefined}
            link={<Link to="/eagle/report">{t('report')}</Link>}>
      {s.data && (incidenti.length === 0 ? <Vuoto testo={t('Nessun incidente nel periodo.')} /> : (
        <TimelineIncidenti incidenti={incidenti} ore={periodo} maxRighe={sinottico ? 12 : 8} />
      ))}
    </Widget>
  )
}

/** Per ogni SLA quanti CI lo rispettano, in una barra a segmenti: rispettato, mancato, senza dati. */
function Sla({ options }: Props) {
  const { s } = useDisponibilita({ hours: options.hours ?? 720 })
  const per = new Map<string, { nome: string; target: number; met: number; missed: number; unknown: number }>()
  for (const c of s.data?.licensed ? s.data.cis : []) {
    if (!c.sla_name) continue
    const v = per.get(c.sla_name) ?? { nome: c.sla_name, target: c.sla_target ?? 0, met: 0, missed: 0, unknown: 0 }
    if (c.sla_met === true) v.met++
    else if (c.sla_met === false) v.missed++
    else v.unknown++
    per.set(c.sla_name, v)
  }
  const righe = [...per.values()].sort((a, b) => b.missed - a.missed || a.nome.localeCompare(b.nome))
  return (
    <Widget titolo={t('Rispetto degli SLA')} sotto={t('ultimi 30 giorni')} caricando={s.loading} errore={s.error} link={<Link to="/eagle/notifiche">{t('SLA')}</Link>}>
      {s.data && (righe.length === 0 ? <Vuoto testo={t('Nessun CI ha uno SLA.')} /> : (
        <ul className="eriga-lista">
          {righe.map((r) => {
            const parti: Segmento[] = [
              { key: 'met', label: t('rispettano'), value: r.met, tone: 'ok' },
              { key: 'missed', label: t('non rispettano'), value: r.missed, tone: 'error' },
              { key: 'unknown', label: t('senza dati'), value: r.unknown, tone: 'muted' },
            ]
            return (
              <li key={r.nome} className="eriga eriga--tre">
                <span className="eriga__nome"><strong>{r.nome}</strong><span className="eriga__sotto"> · {t('traguardo {v} %', { v: r.target })}</span></span>
                <span className={`eriga__valore mono eriga__valore--${r.missed > 0 ? 'error' : 'ok'}`}>{r.met}/{r.met + r.missed + r.unknown}</span>
                <span className="eriga__barra"><Segmenti parti={parti} altezza={8} /></span>
              </li>
            )
          })}
        </ul>
      ))}
    </Widget>
  )
}

// ------------------------------------------------------------------ le sonde

/**
 * Chi legge: per ogni sonda il ruolo, i CI che legge come principale e come
 * secondaria (segmenti) e la quota di letture in ritardo, che è il segno che
 * la sonda non ce la fa — oltre il 2 % è un avviso, oltre il 20 % un
 * problema, e il Control Plane sposta il carico da solo.
 */
function Sonde() {
  const s = usePolling(() => api.eagleSonde(), ogniMinuto)
  const righe = s.data?.licensed ? s.data.probes : []
  return (
    <Widget titolo={t('Sonde di Eagle')} caricando={s.loading} errore={s.error} link={<Link to="/eagle/sonde">{t('apri')}</Link>}>
      {s.data && (righe.length === 0 ? <Vuoto testo={t('Nessuna sonda arruolata.')} /> : (
        <ul className="eriga-lista">
          {righe.map((p) => {
            const quota = p.load && p.load.readings ? Math.round((p.load.late / p.load.readings) * 100) : null
            const tono: Tono = quota == null ? 'muted' : quota > 20 ? 'error' : quota > 2 ? 'warn' : 'ok'
            const ruolo = p.role === 'dedicated' ? t('dedicata') : p.role === 'off' ? t('esclusa') : t('normale')
            const parti: Segmento[] = [
              { key: 'p', label: t('CI principali'), value: p.primary_cis, tone: 'info' },
              { key: 's', label: t('CI secondari'), value: p.secondary_cis, tone: 'muted' },
            ]
            return (
              <li key={p.id} className="eriga eriga--tre">
                <span className="eriga__nome">
                  <PuntoStato stato={p.alive === null ? 'pending' : p.alive ? 'ok' : 'down'} /><strong>{p.name}</strong>
                  <span className="eriga__sotto"> · {ruolo} · {t('{p} principali · {s} secondari', { p: p.primary_cis, s: p.secondary_cis })}{p.max_params != null ? ` · ${t('tetto {n}', { n: p.max_params })}` : ''}</span>
                  {p.verdict && <> <VerdettoSonda v={p.verdict} /></>}
                </span>
                <span className="eriga__valore">
                  {p.alive === null ? <span className="muted">{t('esclusa da Eagle')}</span>
                    : quota == null ? <span className="muted">—</span>
                      : <span className={`badge badge--${tono}`}>{t('{n}% in ritardo', { n: quota })}</span>}
                </span>
                <span className="eriga__barra">
                  {quota != null
                    ? <BarraSoglie valore={quota} massimo={Math.max(25, quota)} warn={2} crit={20} tono={tono} unit="%" testo={t('{n}% di letture in ritardo', { n: quota })} />
                    : <Segmenti parti={parti} altezza={8} />}
                </span>
              </li>
            )
          })}
        </ul>
      ))}
    </Widget>
  )
}

// ------------------------------------------------------------------ catalogo

export interface DefinizioneEagle {
  tipo: string
  label: () => string
  descrizione: () => string
  size: MisuraWidget
  tall?: boolean
  opzioni?: OpzioneEagle[]
  Componente: (p: Props) => JSX.Element
}

export const CATALOGO_EAGLE: DefinizioneEagle[] = [
  { tipo: 'eagle-valore', size: 'm', tall: true, label: () => t('Valore di un parametro'), descrizione: () => t('Il valore attuale di un parametro di un CI, grande e nel colore dello stato, o solo lo stato; sotto, l’ultima ora.'), opzioni: [CI_PARAM, MODO_VALORE, SPARK_VALORE], Componente: (p) => <Valore {...p} /> },
  { tipo: 'eagle-latenza', size: 'l', tall: true, label: () => t('Latenza verso un indirizzo'), descrizione: () => t('Il tempo di risposta verso un IP pubblico (o un capo remoto), misurato da una sonda a scelta.'), opzioni: [BERSAGLIO, ORE_EAGLE, SCALA_Y, Y_MIN, Y_MAX], Componente: (p) => <Latenza {...p} /> },
  { tipo: 'eagle-hero', size: 'xl', label: () => t('Come stanno le cose'), descrizione: () => t('I numeri che contano, l’andamento degli allarmi e i parametri per stato, in una riga.'), opzioni: [ORE_EAGLE], Componente: (p) => <Hero {...p} /> },
  { tipo: 'eagle-parete', size: 'xl', tall: true, label: () => t('Parete dei CI'), descrizione: () => t('Una tessera per CI, colorata dal suo stato peggiore, con i parametri per stato.'), opzioni: [FILTRO_PARETE, TAG_PARETE, SPARK_PARETE, PAGINE_PARETE, ORE_EAGLE], Componente: (p) => <Parete {...p} /> },
  { tipo: 'eagle-grafico', size: 'l', tall: true, label: () => t('Grafico di un parametro'), descrizione: () => t('L’andamento di un parametro di un CI a tua scelta; per un’interfaccia anche il traffico in Mbit/s.'), opzioni: [CI_PARAM, MISURA_GRAFICO, ORE_SERIE, SCALA_Y, Y_MIN, Y_MAX], Componente: (p) => <Grafico {...p} /> },
  { tipo: 'eagle-grafico-libero', size: 'l', tall: true, label: () => t('Grafico personalizzato'), descrizione: () => t('Le serie che scegli — anche di CI diversi — a linee, aree, barre o impilate.'), opzioni: [SERIE_GRAFICO, TIPO_GRAFICO, FASCIA, SOGLIE, ORE_SERIE, SCALA_Y, Y_MIN, Y_MAX], Componente: (p) => <GraficoLibero {...p} /> },
  { tipo: 'eagle-link', size: 'l', tall: true, label: () => t('Link'), descrizione: () => t('Un collegamento fra due interfacce: i due versi in Mbit/s, l’utilizzo con le soglie a ogni capo, lo stato, il mini-grafico. Il capo B si propone dall’adiacenza.'), opzioni: [CAPI_LINK, ORE_SPARK], Componente: (p) => <WidgetLink {...p} /> },
  { tipo: 'eagle-mappa', size: 'xl', tall: true, label: () => t('Mappa con lo stato'), descrizione: () => t('Una vista di topologia salvata, colorata da Eagle: i nodi dallo stato, gli archi dall’utilizzo delle interfacce.'), opzioni: [VISTA_TOPOLOGIA, ARCHI_UTILIZZO, ALTEZZA_MAPPA], Componente: (p) => <MappaEagle {...p} /> },
  { tipo: 'eagle-parete-link', size: 'xl', tall: true, label: () => t('Parete dei link'), descrizione: () => t('Una scheda per interfaccia: descrizione della porta, stato, traffico in e out, utilizzo con le soglie e il mini-grafico.'), opzioni: [SERIE_LINK, MOSTRA_LINK, ORE_SPARK], Componente: (p) => <PareteLink {...p} /> },
  { tipo: 'eagle-top', size: 'l', tall: true, label: () => t('Parametri più alti'), descrizione: () => t('La classifica dei valori attuali con le soglie e l’andamento: CPU, memoria, volumi, interfacce…'), opzioni: [TIPO_TOP, ORE_SPARK], Componente: (p) => <Top {...p} /> },
  { tipo: 'eagle-disponibilita', size: 'm', tall: true, label: () => t('Disponibilità più bassa'), descrizione: () => t('I CI meno disponibili nel periodo, con il traguardo dello SLA.'), opzioni: [ORE_EAGLE], Componente: (p) => <Disponibilita {...p} /> },
  { tipo: 'eagle-incidenti', size: 'l', tall: true, label: () => t('Incidenti recenti'), descrizione: () => t('I tratti in cui un CI non rispondeva, sul filo del tempo.'), opzioni: [ORE_EAGLE], Componente: (p) => <Incidenti {...p} /> },
  { tipo: 'eagle-storico', size: 'xl', tall: true, label: () => t('Storico degli allarmi'), descrizione: () => t('Gli episodi di allarme del periodo, anche quelli già rientrati: su cosa, quando, per quanto. Un clic apre il grafico.'), opzioni: [ORE_STORICO, FILTRO_STORICO], Componente: (p) => <StoricoAllarmi {...p} /> },
  { tipo: 'eagle-sla', size: 'm', label: () => t('Rispetto degli SLA'), descrizione: () => t('Per ogni SLA quanti CI lo rispettano, negli ultimi 30 giorni.'), Componente: (p) => <Sla {...p} /> },
  { tipo: 'eagle-sonde', size: 'l', label: () => t('Sonde di Eagle'), descrizione: () => t('Chi legge, con quale ruolo e quale carico.'), Componente: () => <Sonde /> },
]
